import { AxiosError } from 'axios';
import { concat, from, of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom, tap } from 'rxjs/operators';
import { combineEpics } from 'redux-observable';
import { AppEpic, InvalidateLevel } from '@types';
import { AuthService } from '@services';
import { reverse } from '@utils';
import { ROUTES } from '@constants';
import { appActions, selectAppCompanyName } from '../app';
import { authActions } from './slice';

const obtainAnonymousTokenEpic: AppEpic = (action$, state$) =>
    action$.pipe(
        filter(authActions.obtainAnonymousToken.match),
        withLatestFrom(state$),
        switchMap(([_, state]) => {
            const companyName = selectAppCompanyName(state);

            return from(AuthService.init(companyName).obtainAnonymousToken()).pipe(
                map(({ data }) => authActions.obtainAnonymousTokenSuccess(data)),
                catchError((error: AxiosError) => {
                    const errorCode = error.response?.status;

                    // non-existent company name
                    if (errorCode === 422) {
                        return concat([
                            authActions.obtainAnonymousTokenFailure(),
                            appActions.invalidateStore({
                                purge: InvalidateLevel.Full,
                                redirect: ROUTES.notFound,
                                redirectByRouter: true,
                            }),
                        ]);
                    }

                    return of(authActions.obtainAnonymousTokenFailure());
                }),
            );
        }),
    );

const sessionLoginEpic: AppEpic = (action$, state$) =>
    action$.pipe(
        filter(authActions.obtainSessionToken.match),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const companyName = selectAppCompanyName(state);

            return from(AuthService.init(companyName).obtainSessionToken()).pipe(
                switchMap(res => {
                    // backend redirect case
                    if (!res.request.responseURL.includes(res.config.url)) {
                        return of(authActions.obtainSessionTokenFailure());
                    }

                    return of(authActions.obtainSessionTokenSuccess(res.data));
                }),
                catchError(() => of(authActions.obtainSessionTokenFailure())),
            );
        }),
    );

const silentLoginEpic: AppEpic = (action$, state$) =>
    action$.pipe(
        filter(authActions.silentLogin.match),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            const companyName = selectAppCompanyName(state);

            return from(AuthService.init(companyName).obtainAuthToken(action.payload)).pipe(
                switchMap(({ data }) =>
                    of(
                        appActions.invalidateStore({
                            purge: InvalidateLevel.Client,
                        }),
                        authActions.silentLoginSuccess(data),
                    ),
                ),
                tap(() => window.history.replaceState(null, '', window.location.pathname)),
                catchError(() => of(authActions.silentLoginFailure())),
            );
        }),
    );

const logoutEpic: AppEpic = (action$, state$) =>
    action$.pipe(
        filter(authActions.logout.match),
        withLatestFrom(state$),
        switchMap(([action, state]) => {
            return from(AuthService.init('').logout()).pipe(
                switchMap(() =>
                    of(
                        appActions.invalidateStore({
                            purge: InvalidateLevel.Full,
                            redirect: reverse('signIn'),
                        }),
                    ),
                ),
            );
        }),
    );

export const authEpics = combineEpics(obtainAnonymousTokenEpic, silentLoginEpic, sessionLoginEpic, logoutEpic);
